import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { GoogleMap, useJsApiLoader, OverlayView } from '@react-google-maps/api'
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { useLanguage } from '../context/LanguageContext'

const NAVY  = '#002E53'
const GOLD  = '#C5A059'
const FONT  = "'Poppins', sans-serif"
const API_URL = import.meta.env.VITE_API_URL || ''
const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''

const DUBAI_CENTER = { lat: 25.1124, lng: 55.2244 }
const MAP_ZOOM = 11
const MAPS_LIBRARIES = [] // must be stable (module-level) to avoid re-init loop

// Dubai dark-luxury map style
const MAP_STYLE = [
  { elementType: 'geometry',           stylers: [{ color: '#0d1b2a' }] },
  { elementType: 'labels.text.fill',   stylers: [{ color: '#8fa8c0' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0d1b2a' }] },
  { featureType: 'administrative',     elementType: 'geometry.stroke', stylers: [{ color: '#1a3550' }] },
  { featureType: 'road',               elementType: 'geometry',        stylers: [{ color: '#1a3550' }] },
  { featureType: 'road.highway',       elementType: 'geometry',        stylers: [{ color: '#1e4068' }] },
  { featureType: 'road.highway',       elementType: 'geometry.stroke', stylers: [{ color: '#0d2640' }] },
  { featureType: 'water',              elementType: 'geometry',        stylers: [{ color: '#071828' }] },
  { featureType: 'poi',                elementType: 'geometry',        stylers: [{ color: '#0f2035' }] },
  { featureType: 'poi.park',           elementType: 'geometry',        stylers: [{ color: '#102030' }] },
  { featureType: 'transit',            elementType: 'geometry',        stylers: [{ color: '#0d1b2a' }] },
  { featureType: 'landscape',          elementType: 'geometry',        stylers: [{ color: '#0d1b2a' }] },
]

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function metricColour(area, metric, allAreas) {
  if (!allAreas.length) return GOLD
  const values = allAreas.map(a => a[metric])
  const min = Math.min(...values)
  const max = Math.max(...values)
  const val = area[metric]
  const ratio = max === min ? 0.5 : (val - min) / (max - min)
  const r = Math.round(0x00 + ratio * (0xC5 - 0x00))
  const g = Math.round(0x2E + ratio * (0xA0 - 0x2E))
  const b = Math.round(0x53 + ratio * (0x59 - 0x53))
  return `rgb(${r},${g},${b})`
}

// ── Color Legend ─────────────────────────────────────────────────────────────
function ColorLegend({ metric, ai }) {
  const low  = (ai.legendLow  || {})[metric]  || 'Low'
  const high = (ai.legendHigh || {})[metric] || 'High'
  return (
    <div style={{ marginTop: '14px', marginBottom: '4px' }}>
      <div style={{
        height: '8px', borderRadius: '99px',
        background: 'linear-gradient(to right, #002E53, #6b7a3c, #C5A059)',
        marginBottom: '5px',
      }} />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: FONT, fontSize: '10px', color: '#8a97a8' }}>{low}</span>
        <span style={{ fontFamily: FONT, fontSize: '10px', color: '#8a97a8' }}>{high}</span>
      </div>
      <div style={{
        marginTop: '8px', padding: '7px 10px',
        background: '#fff8f0', borderRadius: '7px',
        border: '1px solid rgba(197,160,89,0.25)',
        fontFamily: FONT, fontSize: '11px', color: '#7a6230', lineHeight: 1.5,
      }}>
        <span style={{ fontWeight: 700, display: 'block', marginBottom: '2px' }}>{ai.legendSelectedArea}</span>
        {ai.legendSelectedDesc}
      </div>
    </div>
  )
}

// ── Custom Map Marker (OverlayView) ──────────────────────────────────────────
function AreaMarker({ area, isSelected, metric, allAreas, onClick }) {
  const colour = metricColour(area, metric, allAreas)
  const bubbleBg    = isSelected ? '#ffffff' : colour
  const bubbleText  = isSelected ? NAVY : '#ffffff'
  const tailColour  = isSelected ? '#ffffff' : colour

  // Build a translated display value for the metric
  const formatters = {
    avgPrice:        v => `AED ${(v/1e6).toFixed(1)}M`,
    rentalYield:     v => `${v.toFixed(1)}%`,
    demandScore:     v => `${v}/100`,
    avgPricePerSqft: v => `AED ${v.toLocaleString()}/sqft`,
  }
  const displayVal = (formatters[metric] || (v => v))(area[metric])

  return (
    <OverlayView
      position={{ lat: area.lat, lng: area.lng }}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <div
        onClick={() => onClick(area)}
        style={{
          transform: 'translate(-50%, -50%)',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          userSelect: 'none',
        }}
      >
        {/* Bubble */}
        <div style={{
          background: bubbleBg,
          color: bubbleText,
          fontFamily: FONT,
          fontSize: '11px',
          fontWeight: 700,
          padding: '6px 10px',
          borderRadius: '20px',
          whiteSpace: 'nowrap',
          boxShadow: isSelected
            ? `0 0 0 3px ${GOLD}, 0 6px 20px rgba(0,0,0,0.5)`
            : '0 2px 8px rgba(0,0,0,0.6)',
          border: `2px solid ${isSelected ? GOLD : 'rgba(255,255,255,0.3)'}`,
          transition: 'all 0.25s',
          transform: isSelected ? 'scale(1.18)' : 'scale(1)',
        }}>
          {area.name}
          <div style={{ fontSize: '9px', opacity: 0.8, fontWeight: 600, marginTop: '1px' }}>
            {displayVal}
          </div>
        </div>
        {/* Pin tail */}
        <div style={{
          width: 0, height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: `8px solid ${tailColour}`,
          marginTop: '-1px',
          filter: isSelected ? `drop-shadow(0 2px 3px rgba(0,0,0,0.3))` : 'none',
        }} />
      </div>
    </OverlayView>
  )
}

// ── Right Panel: Area Intelligence ───────────────────────────────────────────
function AreaPanel({ area, mode, allAreas, onClose, ai }) {
  const [insight, setInsight] = useState('')
  const [insightLoading, setInsightLoading] = useState(false)
  const [insightError, setInsightError] = useState(null)
  const insightFetched = useRef(false)

  useEffect(() => {
    if (!area) return
    setInsight('')
    setInsightError(null)
    insightFetched.current = false
  }, [area?.name])

  const fetchInsight = useCallback(async () => {
    if (!area || insightFetched.current || insightLoading) return
    insightFetched.current = true
    setInsightLoading(true)
    setInsightError(null)
    try {
      const res = await fetch(`${API_URL}/api/areas/insight`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          area: area.name,
          avgPrice: area.avgPrice,
          rentalYield: area.rentalYield,
          demandScore: area.demandScore,
          mode,
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || `Server error ${res.status}`)
      }
      const data = await res.json()
      setInsight(data.insight || '')
    } catch (err) {
      setInsightError(err.message)
    } finally {
      setInsightLoading(false)
    }
  }, [area, mode])

  if (!area) return null

  const kpis = [
    { label: ai.kpiAvgPrice,      value: `AED ${(area.avgPrice / 1e6).toFixed(1)}M`, icon: '💰' },
    { label: ai.kpiPricePerSqft,  value: `AED ${area.avgPricePerSqft?.toLocaleString()}`, icon: '📐' },
    { label: ai.kpiGrossYield,    value: `${area.rentalYield}%`, icon: '📈' },
    { label: ai.kpiDemandScore,   value: `${area.demandScore}/100`, icon: '🔥' },
  ]

  return (
    <div style={{
      width: '340px', flexShrink: 0,
      background: '#fff',
      borderLeft: `4px solid ${GOLD}`,
      display: 'flex', flexDirection: 'column',
      height: '100%',
      overflowY: 'auto',
      position: 'relative',
      animation: 'slideInRight 0.28s ease',
    }}>
      <style>{`
        @keyframes slideInRight { from { opacity: 0; transform: translateX(30px); } to { opacity: 1; transform: translateX(0); } }
        .ai-insight-text { line-height: 1.75; }
        .kpi-chip:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(197,160,89,0.18); }
      `}</style>

      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${NAVY} 0%, #001426 100%)`,
        padding: '24px 20px 20px',
        position: 'relative',
      }}>
        <button onClick={onClose} style={{
          position: 'absolute', top: '16px', right: '16px',
          background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)',
          color: '#fff', width: '30px', height: '30px', borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', fontSize: '14px', fontFamily: FONT,
        }}>✕</button>
        <div style={{ fontSize: '28px', marginBottom: '6px' }}>{area.icon}</div>
        <div style={{ fontFamily: FONT, fontSize: '20px', fontWeight: 700, color: '#fff', marginBottom: '4px' }}>
          {area.name}
        </div>
        <div style={{ fontFamily: FONT, fontSize: '12px', color: GOLD, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase' }}>
          Dubai, UAE · {area.count} {ai.activeProjects}
        </div>
      </div>

      <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* KPI Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          {kpis.map((kpi, i) => (
            <div key={i} className="kpi-chip" style={{
              background: '#f8fbfe',
              border: `1px solid rgba(0,46,83,0.08)`,
              borderRadius: '12px', padding: '14px 12px',
              transition: 'all 0.2s', cursor: 'default',
            }}>
              <div style={{ fontSize: '18px', marginBottom: '4px' }}>{kpi.icon}</div>
              <div style={{ fontFamily: FONT, fontSize: '14px', fontWeight: 700, color: NAVY }}>{kpi.value}</div>
              <div style={{ fontFamily: FONT, fontSize: '10px', color: '#8a97a8', fontWeight: 500, marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{kpi.label}</div>
            </div>
          ))}
        </div>

        {/* Nearby Landmarks */}
        <div>
          <div style={{ fontFamily: FONT, fontSize: '12px', fontWeight: 700, color: GOLD, letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: '10px' }}>
            {ai.nearbyLandmarks}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(area.landmarks || []).map((lm, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                background: '#f8fbfe', borderRadius: '8px', padding: '9px 12px',
                border: '1px solid rgba(0,46,83,0.06)',
              }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: GOLD, flexShrink: 0 }} />
                <span style={{ fontFamily: FONT, fontSize: '13px', color: NAVY, fontWeight: 500 }}>{lm}</span>
              </div>
            ))}
          </div>
        </div>

        {/* AI Insight */}
        <div style={{
          background: `linear-gradient(135deg, rgba(0,46,83,0.04) 0%, rgba(197,160,89,0.06) 100%)`,
          border: `1px solid rgba(197,160,89,0.2)`,
          borderRadius: '12px', padding: '16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '8px' }}>
            <div style={{ fontFamily: FONT, fontSize: '12px', fontWeight: 700, color: GOLD, letterSpacing: '1.2px', textTransform: 'uppercase' }}>
              {ai.aiInsightTitle}
            </div>
            {!insight && !insightLoading && (
              <button
                onClick={fetchInsight}
                style={{
                  background: NAVY, color: '#fff', border: 'none',
                  fontFamily: FONT, fontSize: '11px', fontWeight: 600,
                  padding: '5px 12px', borderRadius: '20px', cursor: 'pointer',
                  transition: 'background 0.2s', whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => e.currentTarget.style.background = GOLD}
                onMouseLeave={e => e.currentTarget.style.background = NAVY}
              >
                {ai.aiGenerate}
              </button>
            )}
          </div>
          {insightLoading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '20px', height: '20px', borderRadius: '50%',
                border: `2px solid ${GOLD}`, borderTopColor: 'transparent',
                animation: 'spin 0.8s linear infinite',
                flexShrink: 0,
              }} />
              <span style={{ fontFamily: FONT, fontSize: '13px', color: '#8a97a8' }}>{ai.aiAnalysing}</span>
            </div>
          )}
          {insightError && (
            <div style={{ fontFamily: FONT, fontSize: '12px', color: '#dc2626', lineHeight: 1.6 }}>
              ⚠️ {insightError.includes('not configured')
                ? ai.aiKeyMissing
                : insightError}
            </div>
          )}
          {insight && !insightLoading && (
            <p className="ai-insight-text" style={{
              fontFamily: FONT, fontSize: '13px', color: NAVY,
              margin: 0, lineHeight: 1.75,
            }}>
              {insight}
            </p>
          )}
          {!insight && !insightLoading && !insightError && (
            <p style={{ fontFamily: FONT, fontSize: '13px', color: '#8a97a8', margin: 0, lineHeight: 1.6 }}>
              {ai.aiPrompt}
            </p>
          )}
        </div>

        {/* CTA */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Link
            to={`/offplan?area=${encodeURIComponent(area.name)}`}
            style={{
              display: 'block', background: GOLD, color: '#fff',
              fontFamily: FONT, fontSize: '13px', fontWeight: 700,
              padding: '13px', borderRadius: '10px', textDecoration: 'none',
              textAlign: 'center', transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#d4af6a'}
            onMouseLeave={e => e.currentTarget.style.background = GOLD}
          >
            {typeof ai.browseOffplan === 'function' ? ai.browseOffplan(area.name) : `Browse Off-Plan in ${area.name} →`}
          </Link>
          <Link
            to={`/properties/buy?area=${encodeURIComponent(area.name)}`}
            style={{
              display: 'block', background: 'transparent', color: NAVY,
              fontFamily: FONT, fontSize: '13px', fontWeight: 600,
              padding: '12px', borderRadius: '10px', textDecoration: 'none',
              textAlign: 'center', border: `1px solid rgba(0,46,83,0.15)`,
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,46,83,0.05)'; e.currentTarget.style.borderColor = NAVY }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(0,46,83,0.15)' }}
          >
            {ai.viewResale}
          </Link>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

// ── Price Trend Chart ─────────────────────────────────────────────────────────
function PriceTrendChart({ areas, selectedAreas, ai }) {
  const displayed = areas.filter(a => selectedAreas.includes(a.slug))
  if (!displayed.length) return null

  const data = MONTHS.map((month, i) => {
    const row = { month }
    displayed.forEach(a => { row[a.name] = a.priceTrend?.[i] ?? null })
    return row
  })

  const AREA_COLOURS = ['#C5A059', '#002E53', '#3c7fc0', '#22C55E', '#EF4444', '#8B5CF6', '#F59E0B']

  return (
    <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', border: '1px solid rgba(0,46,83,0.08)' }}>
      <div style={{ marginBottom: '20px' }}>
        <span style={{
          display: 'inline-block', background: `${GOLD}18`, color: GOLD,
          fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
          padding: '4px 12px', borderRadius: '50px', textTransform: 'uppercase',
          border: `1px solid ${GOLD}35`, marginBottom: '8px',
        }}>{ai.priceTrendBadge}</span>
        <div style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY }}>
          {ai.priceTrendTitle}
        </div>
        <div style={{ fontFamily: FONT, fontSize: '13px', color: '#8a97a8', marginTop: '4px' }}>
          {ai.priceTrendSub}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart key={selectedAreas.join(',')} data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,46,83,0.06)" />
          <XAxis dataKey="month" tick={{ fontFamily: FONT, fontSize: 11, fill: '#8a97a8' }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={v => `${v.toLocaleString()}`} tick={{ fontFamily: FONT, fontSize: 11, fill: '#8a97a8' }} axisLine={false} tickLine={false} width={65} />
          <Tooltip
            contentStyle={{ fontFamily: FONT, borderRadius: '10px', border: `1px solid ${GOLD}30`, fontSize: '13px', backgroundColor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            itemStyle={{ padding: '3px 0' }}
            wrapperStyle={{ zIndex: 100 }}
            formatter={(val, name) => [`AED ${val?.toLocaleString()}/sqft`, name]}
          />
          <Legend wrapperStyle={{ fontFamily: FONT, fontSize: '13px', paddingTop: '16px' }} />
          {displayed.map((a, i) => (
            <Line
              key={a.slug}
              type="monotone"
              dataKey={a.name}
              stroke={AREA_COLOURS[i % AREA_COLOURS.length]}
              strokeWidth={2.5}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

// ── Rental Yield Bar Chart ────────────────────────────────────────────────────
function RentalYieldChart({ areas, ai }) {
  const data = areas.map(a => ({ name: a.name.split(' ').slice(-1)[0], fullName: a.name, yield: a.rentalYield }))
  const tooltipFn = typeof ai.tooltipYield === 'function'
    ? ai.tooltipYield
    : (val, name) => [`${val}% gross yield`, name]

  return (
    <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', border: '1px solid rgba(0,46,83,0.08)' }}>
      <div style={{ marginBottom: '20px' }}>
        <span style={{
          display: 'inline-block', background: `${GOLD}18`, color: GOLD,
          fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
          padding: '4px 12px', borderRadius: '50px', textTransform: 'uppercase',
          border: `1px solid ${GOLD}35`, marginBottom: '8px',
        }}>{ai.rentalYieldBadge}</span>
        <div style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY }}>
          {ai.rentalYieldTitle}
        </div>
        <div style={{ fontFamily: FONT, fontSize: '13px', color: '#8a97a8', marginTop: '4px' }}>
          {ai.rentalYieldSub}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,46,83,0.06)" vertical={false} />
          <XAxis dataKey="name" tick={{ fontFamily: FONT, fontSize: 11, fill: '#8a97a8' }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={v => `${v}%`} tick={{ fontFamily: FONT, fontSize: 11, fill: '#8a97a8' }} axisLine={false} tickLine={false} domain={[0, 12]} />
          <Tooltip
            cursor={{ fill: 'rgba(0,46,83,0.04)' }}
            contentStyle={{ fontFamily: FONT, borderRadius: '10px', border: `1px solid ${GOLD}30`, fontSize: '13px', backgroundColor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            itemStyle={{ padding: '3px 0' }}
            wrapperStyle={{ zIndex: 100 }}
            formatter={(val, _, props) => tooltipFn(val, props.payload.fullName)}
          />
          <Bar dataKey="yield" fill={GOLD} radius={[6, 6, 0, 0]} maxBarSize={52} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

// ── Comparison Table ─────────────────────────────────────────────────────────
function ComparisonTable({ areas, ai }) {
  const [selected, setSelected] = useState([])

  function toggleArea(slug) {
    setSelected(prev =>
      prev.includes(slug) ? prev.filter(s => s !== slug) : prev.length >= 3 ? prev : [...prev, slug]
    )
  }

  const compared = areas.filter(a => selected.includes(a.slug))

  const rows = [
    { label: ai.rowAvgPrice,        key: 'avgPrice',         fmt: v => `AED ${(v/1e6).toFixed(2)}M` },
    { label: ai.rowPricePerSqft,    key: 'avgPricePerSqft',  fmt: v => `AED ${v.toLocaleString()}` },
    { label: ai.rowGrossYield,      key: 'rentalYield',      fmt: v => `${v.toFixed(1)}%` },
    { label: ai.rowDemandScore,     key: 'demandScore',      fmt: v => `${v}/100` },
    { label: ai.rowActiveProjects,  key: 'count',            fmt: v => v },
    { label: ai.rowMinEntry,        key: 'minPrice',         fmt: v => `AED ${(v/1e6).toFixed(1)}M` },
    { label: ai.rowMaxPrice,        key: 'maxPrice',         fmt: v => `AED ${(v/1e6).toFixed(1)}M` },
  ]

  return (
    <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', border: '1px solid rgba(0,46,83,0.08)' }}>
      <div style={{ marginBottom: '24px' }}>
        <span style={{
          display: 'inline-block', background: `${GOLD}18`, color: GOLD,
          fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
          padding: '4px 12px', borderRadius: '50px', textTransform: 'uppercase',
          border: `1px solid ${GOLD}35`, marginBottom: '8px',
        }}>{ai.comparisonBadge}</span>
        <div style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY }}>
          {ai.comparisonTitle}
        </div>
        <div style={{ fontFamily: FONT, fontSize: '13px', color: '#8a97a8', marginTop: '4px' }}>
          {ai.comparisonSub}
        </div>
      </div>

      {/* Area selector chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '24px' }}>
        {areas.map(a => {
          const isOn = selected.includes(a.slug)
          return (
            <button key={a.slug} onClick={() => toggleArea(a.slug)} style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: isOn ? NAVY : '#f4f7fb',
              color: isOn ? '#fff' : NAVY,
              border: `1px solid ${isOn ? NAVY : 'rgba(0,46,83,0.12)'}`,
              fontFamily: FONT, fontSize: '13px', fontWeight: 600,
              padding: '8px 16px', borderRadius: '50px', cursor: 'pointer',
              transition: 'all 0.18s',
            }}>
              <span>{a.icon}</span>
              {a.name}
            </button>
          )
        })}
      </div>

      {/* Comparison Table */}
      {compared.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
            <thead>
              <tr>
                <th style={{
                  textAlign: 'left', padding: '12px 16px',
                  fontFamily: FONT, fontSize: '12px', fontWeight: 700, color: '#8a97a8',
                  textTransform: 'uppercase', letterSpacing: '0.8px',
                  borderBottom: '2px solid rgba(0,46,83,0.08)',
                }}>{ai.colMetric}</th>
                {compared.map(a => (
                  <th key={a.slug} style={{
                    textAlign: 'center', padding: '12px 16px',
                    fontFamily: FONT, fontSize: '13px', fontWeight: 700, color: NAVY,
                    borderBottom: '2px solid rgba(0,46,83,0.08)',
                    minWidth: '140px',
                  }}>
                    <div style={{ fontSize: '18px', marginBottom: '4px' }}>{a.icon}</div>
                    {a.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={row.key} style={{ background: ri % 2 === 0 ? '#f8fbfe' : '#fff' }}>
                  <td style={{
                    padding: '13px 16px',
                    fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: NAVY,
                    borderBottom: '1px solid rgba(0,46,83,0.05)',
                  }}>{row.label}</td>
                  {compared.map(a => {
                    const values = compared.map(x => x[row.key])
                    let isBest = false
                    if (row.key === 'minPrice') isBest = a[row.key] === Math.min(...values)
                    else if (row.key !== 'maxPrice') isBest = a[row.key] === Math.max(...values)

                    return (
                      <td key={a.slug} style={{
                        padding: '13px 16px', textAlign: 'center',
                        fontFamily: FONT, fontSize: '14px', fontWeight: isBest ? 700 : 500,
                        color: isBest ? GOLD : NAVY,
                        borderBottom: '1px solid rgba(0,46,83,0.05)',
                        borderLeft: '1px solid rgba(0,46,83,0.05)',
                      }}>
                        {row.fmt(a[row.key])}
                        {isBest && row.key !== 'maxPrice' && row.key !== 'minPrice' && (
                          <span style={{ display: 'block', fontSize: '10px', color: GOLD, fontWeight: 600, marginTop: '2px' }}>{ai.best}</span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{
          textAlign: 'center', padding: '40px 20px',
          background: '#f8fbfe', borderRadius: '12px',
          border: '2px dashed rgba(0,46,83,0.1)',
          fontFamily: FONT, fontSize: '14px', color: '#8a97a8',
        }}>
          {ai.comparisonPlaceholder}
        </div>
      )}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
function AreaIntelligencePage() {
  const [searchParams] = useSearchParams()
  const { t } = useLanguage()
  const ai = t('areaIntelligence')

  // Build translated METRIC_CONFIG
  const METRIC_CONFIG = {
    avgPrice:        { label: ai.metricAvgPrice,      unit: 'AED', format: v => `AED ${(v/1e6).toFixed(1)}M` },
    rentalYield:     { label: ai.metricRentalYield,   unit: '%',   format: v => `${v.toFixed(1)}%` },
    demandScore:     { label: ai.metricDemand,        unit: '/100',format: v => `${v}/100` },
    avgPricePerSqft: { label: ai.metricPricePerSqft,  unit: 'AED/sqft', format: v => `AED ${v.toLocaleString()}/sqft` },
  }

  const [areas, setAreas] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedArea, setSelectedArea] = useState(null)
  const [heatmapMetric, setHeatmapMetric] = useState('avgPricePerSqft')
  const [chartSelectedAreas, setChartSelectedAreas] = useState([])
  const [filterOpen, setFilterOpen] = useState(false)

  const urlArea = searchParams.get('area')

  const { isLoaded: mapsLoaded, loadError: mapsError } = useJsApiLoader({
    googleMapsApiKey: MAPS_KEY,
    libraries: MAPS_LIBRARIES,
  })

  useEffect(() => {
    setLoading(true)
    fetch(`${API_URL}/api/areas/stats`)
      .then(r => { if (!r.ok) throw new Error(`Server error ${r.status}`); return r.json() })
      .then(data => {
        const areaList = data.areas || []
        setAreas(areaList)
        setChartSelectedAreas(areaList.map(a => a.slug))

        if (urlArea) {
          const match = areaList.find(a =>
            a.slug === urlArea ||
            a.name.toLowerCase().replace(/\s+/g, '-') === urlArea.toLowerCase()
          )
          if (match) setSelectedArea(match)
        }
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }, [urlArea])

  useEffect(() => {
    if (selectedArea && !chartSelectedAreas.includes(selectedArea.slug)) {
      setSelectedArea(null)
    }
  }, [chartSelectedAreas, selectedArea])

  return (
    <div style={{ fontFamily: FONT, background: '#f4f7fb', minHeight: '100vh' }}>
      <Navbar />

      {/* ══ HERO ══════════════════════════════════════════════════════════ */}
      <div style={{
        background: `linear-gradient(135deg, ${NAVY} 0%, #001426 60%, #002040 100%)`,
        padding: '130px 40px 60px',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Decorative orbs */}
        <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '400px', height: '400px', borderRadius: '50%', background: `${GOLD}08`, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-60px', left: '10%', width: '280px', height: '280px', borderRadius: '50%', background: `${GOLD}06`, pointerEvents: 'none' }} />

        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: `${GOLD}22`, border: `1px solid ${GOLD}50`,
            color: GOLD, fontFamily: FONT, fontSize: '11px', fontWeight: 700,
            letterSpacing: '2px', textTransform: 'uppercase',
            padding: '6px 16px', borderRadius: '50px', marginBottom: '22px',
          }}>
            🗺️ {ai.heroBadge}
          </div>
          <h1 style={{
            fontFamily: FONT, fontSize: 'clamp(32px, 5vw, 58px)',
            fontWeight: 800, color: '#fff', margin: '0 0 16px', lineHeight: 1.1, letterSpacing: '-1px',
          }}>
            {ai.heroLine1}<br />
            <span style={{ color: GOLD }}>{ai.heroAccent}</span>
          </h1>
          <p style={{
            fontFamily: FONT, fontSize: '17px', color: 'rgba(255,255,255,0.65)',
            margin: '0', lineHeight: 1.7, maxWidth: '560px',
          }}>
            {ai.heroSub}
          </p>
        </div>
      </div>

      {/* ══ MAP + SIDEBAR + PANEL ════════════════════════════════════════ */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px 24px', boxSizing: 'border-box' }}>

        {/* Mobile filter toggle button */}
        <button
          className="ai-filter-toggle"
          onClick={() => setFilterOpen(o => !o)}
          style={{
            display: 'none',
            alignItems: 'center', gap: '8px',
            marginBottom: '12px',
            background: filterOpen ? NAVY : GOLD,
            color: '#fff',
            border: 'none',
            borderRadius: '10px',
            padding: '10px 18px',
            fontFamily: FONT,
            fontSize: '13px',
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="4" y1="6" x2="20" y2="6"/>
            <line x1="4" y1="12" x2="14" y2="12"/>
            <line x1="4" y1="18" x2="10" y2="18"/>
          </svg>
          {filterOpen ? (ai.filterClose || 'Hide Filters') : (ai.filterOpen || 'Filters & Metrics')}
        </button>

        <div style={{
          display: 'flex', gap: '20px', alignItems: 'stretch',
          marginBottom: '32px',
        }} className="ai-main-row">

          {/* Filter Sidebar */}
          <div style={{
            width: '240px', flexShrink: 0,
            background: '#fff', borderRadius: '16px',
            border: '1px solid rgba(0,46,83,0.08)',
            padding: '22px 18px',
            position: 'sticky', top: '90px',
            boxShadow: '0 4px 16px rgba(0,46,83,0.06)',
          }} className={`ai-sidebar${filterOpen ? ' ai-sidebar--open' : ''}`}>

            <div style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 700, color: NAVY, marginBottom: '16px', letterSpacing: '0.5px' }}>
              🎛️ {ai.filterTitle}
            </div>

            {/* Heatmap metric */}
            <div style={{ marginBottom: '8px' }}>
              <div style={{ fontFamily: FONT, fontSize: '11px', fontWeight: 700, color: '#8a97a8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' }}>
                {ai.heatmapLabel}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {Object.entries(METRIC_CONFIG).map(([key, cfg]) => (
                  <button key={key} onClick={() => setHeatmapMetric(key)} style={{
                    textAlign: 'left', background: heatmapMetric === key ? `${NAVY}12` : 'transparent',
                    border: `1px solid ${heatmapMetric === key ? NAVY : 'rgba(0,46,83,0.1)'}`,
                    color: heatmapMetric === key ? NAVY : '#6b7a8d',
                    fontFamily: FONT, fontSize: '13px', fontWeight: heatmapMetric === key ? 700 : 500,
                    padding: '9px 12px', borderRadius: '8px', cursor: 'pointer',
                    transition: 'all 0.18s',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    {cfg.label}
                    {heatmapMetric === key && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: GOLD, flexShrink: 0 }} />}
                  </button>
                ))}
              </div>
              <ColorLegend metric={heatmapMetric} ai={ai} />
            </div>

            {/* Chart Areas selector */}
            <div style={{ marginTop: '24px' }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: '10px',
              }}>
                <div style={{ fontFamily: FONT, fontSize: '11px', fontWeight: 700, color: '#8a97a8', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  {ai.chartAreasLabel}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    onClick={() => setChartSelectedAreas(areas.map(a => a.slug))}
                    style={{
                      fontFamily: FONT, fontSize: '10px', fontWeight: 600,
                      color: NAVY, background: 'transparent',
                      border: `1px solid rgba(0,46,83,0.2)`,
                      borderRadius: '4px', padding: '2px 6px', cursor: 'pointer',
                    }}
                  >{ai.selectAll}</button>
                  <button
                    onClick={() => setChartSelectedAreas([])}
                    style={{
                      fontFamily: FONT, fontSize: '10px', fontWeight: 600,
                      color: '#8a97a8', background: 'transparent',
                      border: '1px solid rgba(0,46,83,0.12)',
                      borderRadius: '4px', padding: '2px 6px', cursor: 'pointer',
                    }}
                  >{ai.selectNone}</button>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {areas.map(a => {
                  const on = chartSelectedAreas.includes(a.slug)
                  return (
                    <label key={a.slug} style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      cursor: 'pointer', fontFamily: FONT, fontSize: '13px',
                      color: on ? NAVY : '#6b7a8d', fontWeight: on ? 600 : 400,
                      userSelect: 'none',
                    }}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => setChartSelectedAreas(prev =>
                          prev.includes(a.slug)
                            ? prev.filter(s => s !== a.slug)
                            : [...prev, a.slug]
                        )}
                        style={{ accentColor: GOLD, width: '15px', height: '15px', cursor: 'pointer', flexShrink: 0 }}
                      />
                      <span style={{ flexShrink: 0 }}>{a.icon}</span>
                      <span style={{ lineHeight: 1.3 }}>{a.name}</span>
                    </label>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Map */}
          <div style={{
            flex: 1, minWidth: 0,
            borderRadius: '16px',
            overflow: 'hidden',
            minHeight: '580px',
            boxShadow: '0 4px 24px rgba(0,46,83,0.12)',
            border: '1px solid rgba(0,46,83,0.08)',
            background: '#0d1b2a',
            position: 'relative',
            display: 'flex', flexDirection: 'column'
          }} className="ai-map-container">
            {!MAPS_KEY ? (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                height: '100%', flexDirection: 'column', gap: '12px',
                fontFamily: FONT, color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: '40px',
              }}>
                <div style={{ fontSize: '40px' }}>🗺️</div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>{ai.mapsKeyMissing}</div>
                <div style={{ fontSize: '13px', maxWidth: '340px', lineHeight: 1.6 }}>
                  {ai.mapsKeyHint}
                </div>
              </div>
            ) : mapsError ? (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                height: '100%', fontFamily: FONT, color: 'rgba(255,255,255,0.5)', flexDirection: 'column', gap: '8px',
              }}>
                <div style={{ fontSize: '36px' }}>⚠️</div>
                <div>{ai.mapFailedLoad}</div>
              </div>
            ) : !mapsLoaded ? (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                height: '100%', fontFamily: FONT, color: 'rgba(255,255,255,0.4)',
              }}>
                {ai.mapLoading}
              </div>
            ) : (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={DUBAI_CENTER}
                zoom={MAP_ZOOM}
                options={{
                  styles: MAP_STYLE,
                  disableDefaultUI: false,
                  zoomControl: true,
                  mapTypeControl: false,
                  streetViewControl: false,
                  fullscreenControl: true,
                  clickableIcons: false,
                }}
              >
                {areas
                  .filter(area => chartSelectedAreas.includes(area.slug))
                  .map(area => (
                  <AreaMarker
                    key={area.slug}
                    area={area}
                    isSelected={selectedArea?.slug === area.slug}
                    metric={heatmapMetric}
                    allAreas={areas}
                    onClick={setSelectedArea}
                    ai={ai}
                  />
                ))}
              </GoogleMap>
            )}
          </div>

          {/* Right panel */}
          {selectedArea && (
            <div className="ai-area-panel" style={{ display: 'flex', flexShrink: 0 }}>
              <AreaPanel
                area={selectedArea}
                mode="buy"
                allAreas={areas}
                onClose={() => setSelectedArea(null)}
                ai={ai}
              />
            </div>
          )}
        </div>

        {/* Loading / Error states */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px', fontFamily: FONT, color: '#8a97a8' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '50%',
              border: `3px solid ${GOLD}`, borderTopColor: 'transparent',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto 16px',
            }} />
            {ai.loading}
          </div>
        )}

        {error && (
          <div style={{
            background: '#fff8f8', borderRadius: '12px', padding: '24px',
            border: '1px solid #fecaca', color: '#991b1b',
            fontFamily: FONT, fontSize: '14px', textAlign: 'center', marginBottom: '24px',
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* ══ CHARTS ═══════════════════════════════════════════════════ */}
        {!loading && areas.length > 0 && (
          <>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr',
              gap: '20px', marginBottom: '20px',
            }} className="ai-charts-grid">
              <PriceTrendChart areas={areas} selectedAreas={chartSelectedAreas} ai={ai} />
              <RentalYieldChart areas={areas} ai={ai} />
            </div>

            {/* ══ COMPARISON TABLE ════════════════════════════════════ */}
            <ComparisonTable areas={areas} ai={ai} />
          </>
        )}
      </div>

      <Footer />

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }

        /* ── Tablet: side-by-side collapses ─────────────────── */
        @media (max-width: 1200px) {
          .ai-main-row {
            flex-direction: column !important;
          }
          .ai-sidebar {
            width: 100% !important;
            position: static !important;
            display: none;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            box-sizing: border-box;
          }
          .ai-sidebar.ai-sidebar--open {
            display: grid !important;
          }
          .ai-filter-toggle {
            display: flex !important;
          }
          .ai-map-container {
            width: 100% !important;
            flex: none !important;
            height: 480px !important;
            min-height: 480px !important;
          }
          .ai-area-panel {
            width: 100% !important;
            border-left: none !important;
            border-top: 4px solid #C5A059 !important;
          }
          .ai-area-panel > div {
            width: 100% !important;
            border-left: none !important;
          }
        }

        /* ── Mobile ─────────────────────────────────────────── */
        @media (max-width: 768px) {
          .ai-map-container {
            flex: none !important;
            height: 400px !important;
            min-height: 400px !important;
          }
        }
        @media (max-width: 600px) {
          .ai-sidebar { grid-template-columns: 1fr !important; }
          .ai-map-container {
            flex: none !important;
            height: 340px !important;
            min-height: 340px !important;
          }
        }
        @media (max-width: 900px) {
          .ai-charts-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  )
}

export default AreaIntelligencePage
